import React from "react";
import {
  faEnvelope,
  faMinusCircle,
  faSeedling,
  faSlash,
  faStopwatch,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Box, Grid, Tooltip } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { CateringType } from "../../../../../models/enums/cateringType";
import { mapCountToProgressStatus } from "../../../../../models/enums/elearningProgressStatus.enum";
import {
  ELearningParticipationStatus,
  ParticipationStatus,
} from "../../../../../models/enums/participationStatus.enum";
import { TrainingType } from "../../../../../models/enums/trainingType.enum";
import { UserType } from "../../../../../models/enums/userType.enum";
import { ParticipantListData } from "../../../../../models/participantData";
import { AppState } from "../../../../../redux";
import { showDialog } from "../../../../../redux/dialogs/actions";
import { cancelReactivated } from "../../../../../redux/participants/actions";
import styles from "../../../../../styles/custom-styles.module.scss";
import CustomCheckbox from "../../../../forms/CustomCheckbox";
import Loader from "../../../../theming/loader/Loader";
import "./Participants.scss";
import { ParticipantsStatus } from "./ParticipantsStatus";

interface ParticipantsItemProps {
  participant: ParticipantListData;
  // eslint-disable-next-line react/no-unused-prop-types
  disabledSelect?: boolean;
  index: number;
  trainingType: TrainingType;
  checkedHandler?: () => void;
  checked: boolean;
}

export const ParticipantsItem: React.FC<ParticipantsItemProps> = (props) => {
  const dispatch = useDispatch();
  const accessibility = useSelector((state: AppState) => state.accessibility);
  const currentUser = useSelector((state: AppState) => state.user.currentUser);
  const { participant, index, trainingType, checkedHandler, checked } = props;
  const isCancelled =
    participant.status === ParticipationStatus.Cancelled ||
    participant.status === ParticipationStatus.CancelledByStaff;
  const isReservedCart = participant.status === ParticipationStatus.ReservedCart;

  const inactivate = () => {
    dispatch(
      showDialog({
        title: `E-Learning-Teilnahme für ${participant.user.fullname} inaktivieren`,
        message: `Möchen Sie den E-Learning-Bearbeitungszeitraum für ${participant.user.fullname} beenden?`,
        action: () => {
          dispatch(cancelReactivated(participant.event.toString(), participant.id));
        },
      })
    );
  };
  const progress = mapCountToProgressStatus(
    participant.elearning_started_count,
    participant.elearning_status === ELearningParticipationStatus.Passed,
    participant.status === ParticipationStatus.CertificateRevoked,
    participant.status === ParticipationStatus.PendingPayment
  );

  return (
    <Grid
      container
      className="participant-list-item"
      style={{ display: "flex", alignItems: "center", border: "none" }}
    >
      {participant.changeStatus ? (
        <Loader height={63} />
      ) : (
        <>
          <Grid
            item
            xs={8}
            sm={participant.status === ParticipationStatus.ReservedCart ? 5 : 8}
          >
            <Grid container>
              <Grid
                item
                xs={2}
                sm={participant.status === ParticipationStatus.ReservedCart ? 3 : 2}
              >
                <CustomCheckbox
                  name={String(participant.id)}
                  onChange={checkedHandler}
                  checked={checked}
                />
              </Grid>
              {participant.reactivated ? (
                <Grid item xs={1}>
                  <Box className="participant-reactivated">
                    <Tooltip
                      placement="right"
                      title={`Die reaktivierte E-Learning-Teilnahme von ${
                        participant.user.fullname
                      } wird am ${new Date(
                        participant.overall_end
                      ).toLocaleDateString()} automatisch wieder inaktiv.`}
                      arrow
                    >
                      <span>
                        <FontAwesomeIcon
                          icon={faStopwatch}
                          size="lg"
                          color={accessibility.monoMode ? "#000" : styles.orange}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title="E-Learning-Teilnahme jetzt inaktivieren"
                      arrow
                    >
                      <span style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                          icon={faMinusCircle}
                          size="lg"
                          color={accessibility.monoMode ? "#000" : styles.primary}
                          onClick={inactivate}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              ) : null}
              <Grid
                item
                xs={8}
                sm={4}
                style={{
                  paddingLeft:
                    participant.status === ParticipationStatus.ReservedCart
                      ? ".4375rem"
                      : "0rem",
                }}
              >
                {`${participant.user.last_name}, ${participant.user.first_name}`}
                {participant.user.academic_title !== null
                  ? `, ${participant.user.academic_title}`
                  : ""}
                <br />
                <Box component="span" className="participant-list-item-secondarytext">
                  {participant.user.email}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={1}>
            {participant.catering_option !== CateringType.Undefined &&
              currentUser?.user_type !== UserType.Speaker &&
              (participant.catering_option === CateringType.NoMeal ? (
                <Box component="span" style={{ position: "relative" }}>
                  <FontAwesomeIcon
                    icon={faUtensils}
                    size="2x"
                    color="var(--text-color)"
                  />
                  <FontAwesomeIcon
                    icon={faSlash}
                    style={{
                      position: "absolute",
                      left: "-0.375rem",
                      top: "-0.75rem",
                    }}
                    color="#f44336"
                    size="2x"
                  />
                </Box>
              ) : participant.catering_option === CateringType.DefaultMeal ? (
                <FontAwesomeIcon icon={faUtensils} size="2x" color="var(--text-color)" />
              ) : (
                participant.catering_option === CateringType.Vegetarian && (
                  <FontAwesomeIcon icon={faSeedling} size="2x" color="#95e917" />
                )
              ))}
          </Grid>
          {trainingType === TrainingType.ELearning && (
            <Grid item xs={12} sm={3}>
              <ParticipantsStatus
                participant={participant}
                index={index}
                trainingType={TrainingType.ELearning}
              />
            </Grid>
          )}
          {trainingType === TrainingType.BlendedLearning && (
            <>
              <Grid item xs={12} sm={3}>
                <ParticipantsStatus
                  participant={participant}
                  index={index}
                  trainingType={TrainingType.DefaultEvent}
                  isPartOfBlendedLearning
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                {!isCancelled && !isReservedCart && (
                  <ParticipantsStatus
                    participant={participant}
                    index={index}
                    trainingType={TrainingType.ELearning}
                    isPartOfBlendedLearning
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={1}>
                <Box display="flex" justifyContent="center">
                  <Box p={1} display="flex" justifyContent="center" alignItems="center">
                    <strong>
                      <span>{progress}</span>
                    </strong>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
          {trainingType === TrainingType.DefaultEvent && (
            <>
              {participant.status === ParticipationStatus.ReservedCart && (
                <Grid item xs={12} sm={3}>
                  Teilnahme angefragt
                  {(new Date(participant.overall_end).getTime() -
                    new Date(participant.last_changed_on).getTime()) /
                    (1000 * 60 * 60 * 24) >
                  3 ? (
                    <Tooltip
                      arrow
                      placement="right"
                      enterDelay={500}
                      leaveDelay={200}
                      style={{ fontSize: "1rem" }}
                      title="Die Anzahl der Tage, in welcher der Nutzer auf die automatische Anfrage reagieren kann"
                    >
                      <Badge
                        badgeContent={
                          3 -
                          Math.round(
                            (Date.now() -
                              new Date(participant.last_changed_on).getTime()) /
                              (1000 * 60 * 60 * 24)
                          )
                        }
                        color="primary"
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        overlap="circular"
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          size="lg"
                          color={
                            accessibility.monoMode ? "#000" : "var(--primary-color)"
                          }
                          style={{
                            marginBottom: ".625rem",
                            marginRight: ".3125rem",
                            marginLeft: ".3125rem",
                          }}
                        />
                      </Badge>
                    </Tooltip>
                  ) : (
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      size="lg"
                      color={accessibility.monoMode ? "#000" : "var(--primary-color)"}
                      style={{
                        marginBottom: ".625rem",
                        marginRight: ".3125rem",
                        marginLeft: ".3125rem",
                      }}
                    />
                  )}
                </Grid>
              )}
              <Grid item xs={12} sm={3}>
                <ParticipantsStatus
                  participant={participant}
                  index={index}
                  trainingType={TrainingType.DefaultEvent}
                />
              </Grid>
            </>
          )}
          <Grid
            container
            className="participant-list-item"
            style={{
              width: "100%",
            }}
          />
        </>
      )}
    </Grid>
  );
};

// TODO no translation yet
